/**
 * DriftWatchCard (extracted from ReleaseOpsTab.jsx in iter84.59.18)
 *
 * Phase 4 sentinel that scans active tournaments hourly for duplicate
 * match docs. The card exposes:
 *   * **Scan now** — explicit one-shot scan; emits toasts for
 *     clean / drifted / new-alerts outcomes.
 *   * **Preview bulk recompute** — dry-run lists all duplicate match
 *     doc IDs across every active tournament without mutating.
 *   * **Commit cleanup** — destructive; removes the duplicate match
 *     docs surfaced by the preview. Gated behind a window.confirm.
 *
 * State is local — nothing is lifted to the parent. The parent
 * `ReleaseOpsTab` just wraps this in a `<CollapsibleCard>` with the
 * Drift Watch title + badge.
 *
 * Touched in iter84.59.18 only to relocate — zero behavior change.
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, Info, ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export function DriftWatchCardContent() {
    const [lastScan, setLastScan] = useState(null);
    const [busy, setBusy] = useState(false);
    const [bulkPreview, setBulkPreview] = useState(null);

    const loadLast = useCallback(async () => {
        try {
            setLastScan(await api.driftWatchLastScan());
        } catch { /* leave empty */ }
    }, []);

    useEffect(() => { loadLast(); }, [loadLast]);

    const scanNow = async () => {
        setBusy(true);
        try {
            const r = await api.driftWatchScan();
            if (r.alerts_emitted > 0) toast.warning(`Found ${r.tournaments_with_drift} tournament(s) with drift — ${r.alerts_emitted} new alert(s) in Audit Log.`);
            else if (r.tournaments_with_drift > 0) toast.info(`${r.tournaments_with_drift} tournament(s) still drifted, but signatures match prior alerts.`);
            else toast.success(`Clean. Scanned ${r.tournaments_scanned} active tournament(s).`);
            await loadLast();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Scan failed");
        }
        setBusy(false);
    };

    const bulkDryRun = async () => {
        setBusy(true);
        try {
            const r = await api.driftWatchBulkRecompute({ dryRun: true });
            setBulkPreview(r);
            if (r.total_duplicates === 0) toast.success("No duplicates found.");
            else toast.warning(`Preview: ${r.total_duplicates} dup(s) across ${r.tournaments_with_changes} tournament(s).`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Preview failed");
        }
        setBusy(false);
    };

    const bulkCommit = async () => {
        if (!window.confirm(`Bulk Recompute will REMOVE ${bulkPreview?.total_duplicates ?? "all"} duplicate match doc(s). Continue?`)) return;
        setBusy(true);
        try {
            const r = await api.driftWatchBulkRecompute({ dryRun: false });
            toast.success(`Done. Removed ${r.total_duplicates} dup(s).`);
            setBulkPreview(null);
            await loadLast();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Commit failed");
        }
        setBusy(false);
    };

    return (
        <div className="space-y-3 text-xs" data-testid="drift-watch-content">
            <p className="text-zinc-500 leading-relaxed">
                Background sentinel that scans active tournaments hourly for duplicate match docs.
                Detections land in <strong className="text-white">Compliance → Audit Log</strong>.
            </p>
            {lastScan?.ran_at ? (
                <div className="brutal-card-light p-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="drift-watch-last-scan">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Last scan</div>
                        <div className="font-mono text-zinc-300 text-[11px]">{lastScan.ran_at.replace("T", " ").slice(0, 19)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Scanned</div>
                        <div className="font-display font-extrabold text-base">{lastScan.tournaments_scanned}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">With drift</div>
                        <div className={`font-display font-extrabold text-base ${lastScan.tournaments_with_drift ? "text-amber-400" : "text-emerald-400"}`}>
                            {lastScan.tournaments_with_drift}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Alerts</div>
                        <div className="font-display font-extrabold text-base">{lastScan.alerts_emitted}</div>
                    </div>
                </div>
            ) : (
                <p className="text-zinc-600 italic">No scan recorded yet.</p>
            )}
            <div className="flex gap-2 flex-wrap">
                <button type="button" onClick={scanNow} disabled={busy} data-testid="drift-watch-scan-now-btn" className="brutal-btn-primary text-xs py-2 disabled:opacity-60">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldAlert className="w-3.5 h-3.5" />}
                    Scan now
                </button>
                <button type="button" onClick={bulkDryRun} disabled={busy} data-testid="drift-watch-bulk-preview-btn" className="brutal-btn-ghost text-xs py-2 disabled:opacity-60">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Info className="w-3.5 h-3.5" />}
                    Preview bulk recompute
                </button>
                {bulkPreview && bulkPreview.total_duplicates > 0 && (
                    <button type="button" onClick={bulkCommit} disabled={busy} data-testid="drift-watch-bulk-commit-btn" className="brutal-btn text-xs py-2 bg-amber-500/10 text-amber-300 border-amber-500/40 hover:bg-amber-500/20 disabled:opacity-60">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        Commit cleanup ({bulkPreview.total_duplicates} dup{bulkPreview.total_duplicates === 1 ? "" : "s"})
                    </button>
                )}
            </div>
            {bulkPreview && (
                <div className="brutal-card-light p-3 space-y-1" data-testid="drift-watch-bulk-preview">
                    <div className="flex justify-between text-zinc-400 text-[11px]">
                        <span>Preview taken</span>
                        <span className="font-mono">{bulkPreview.ran_at.replace("T", " ").slice(0, 19)}</span>
                    </div>
                    {bulkPreview.total_duplicates === 0 ? (
                        <p className="text-emerald-400 text-xs">No duplicates anywhere.</p>
                    ) : (
                        <ul className="space-y-1 max-h-48 overflow-y-auto">
                            {bulkPreview.rows.map((row) => (
                                <li key={row.tournament_id} className="text-[11px] text-zinc-300 flex justify-between font-mono">
                                    <span className="truncate pr-2">{row.tournament_name || row.tournament_id.slice(0, 8) + "…"}</span>
                                    <span className="text-amber-300 shrink-0">
                                        {row.error ? `err: ${row.error.slice(0, 30)}` : `${row.duplicate_ids?.length ?? 0} dup(s)`}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
